import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import AdminModal, { ModalFooterActions } from '../../admin/ui/AdminModal';
import AdminPersonnelModal, {
  ModalFooterActions as ApModalFooterActions
} from '../ui/AdminPersonnelModal';
import MemberCardSheet from '../../members/MemberCardSheet';
import {
  buildMemberCardPdfFilename,
  exportMemberCardToPdf
} from '../../members/memberCardExport';
import receptionService from '../../../services/receptionService';
import '../../members/MemberCardSheet.css';

function CardModalActions({
  isAdminUi,
  member,
  exportingPdf,
  onPrint,
  onExportPdf,
  onEdit,
  onHide
}) {
  const ghostClass = isAdminUi ? 'adm-modal-btn adm-modal-btn--ghost' : 'ap-modal-btn ap-modal-btn--ghost';
  const primaryClass = isAdminUi
    ? 'adm-modal-btn adm-modal-btn--primary'
    : 'ap-modal-btn ap-modal-btn--edit';
  const busy = exportingPdf;

  const actions = (
    <>
      <button
        type="button"
        className={ghostClass}
        onClick={onPrint}
        disabled={!member || busy}
      >
        <Icon name="desktop" size={14} />
        Imprimer
      </button>
      <button
        type="button"
        className={ghostClass}
        onClick={onExportPdf}
        disabled={!member || busy}
      >
        {busy ? (
          <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
        ) : (
          <Icon name="download" size={14} />
        )}
        Exporter PDF
      </button>
      {onEdit && member ? (
        <button
          type="button"
          className={primaryClass}
          onClick={() => {
            onEdit(member);
            onHide();
          }}
          disabled={busy}
        >
          <Icon name="edit" size={14} />
          Modifier
        </button>
      ) : null}
    </>
  );

  if (isAdminUi) {
    return (
      <ModalFooterActions onCancel={onHide} cancelLabel="Fermer" primary={actions} />
    );
  }

  return (
    <ApModalFooterActions onCancel={onHide} cancelLabel="Fermer" primary={actions} />
  );
}

const MemberDetailsModal = ({
  show,
  onHide,
  member,
  onEdit,
  getCompanyName,
  formatDate,
  companies = [],
  variant = 'personnel'
}) => {
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const isAdminUi = variant === 'admin';
  const fullName = member ? `${member.nom || ''} ${member.prenom || ''}`.trim() : '';

  useEffect(() => {
    if (!show || !member?.matricule) {
      setVisits([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadingVisits(true);
      try {
        const response = await receptionService.getVisitHistoryByMatricule(member.matricule, {
          limit: 12
        });
        if (!cancelled) {
          setVisits(response?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Historique visites carte adhérent:', error);
          setVisits([]);
        }
      } finally {
        if (!cancelled) setLoadingVisits(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, member?.matricule]);

  const handlePrint = useCallback(() => {
    const cardRoot = document.getElementById('member-card-print-root');
    const landscapeRoot = cardRoot?.closest('.member-card') || cardRoot;
    landscapeRoot?.classList.add('member-card--landscape');
    document.body.classList.add('member-card-print-mode');

    const cleanup = () => {
      document.body.classList.remove('member-card-print-mode');
      landscapeRoot?.classList.remove('member-card--landscape');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1000);
  }, []);

  const handleExportPdf = useCallback(async () => {
    const cardEl = document.getElementById('member-card-print-root');
    if (!cardEl || !member) return;

    setExportingPdf(true);
    try {
      await exportMemberCardToPdf(cardEl, {
        filename: buildMemberCardPdfFilename(member)
      });
    } catch (error) {
      console.error('Export PDF carte adhérent:', error);
      window.alert("Impossible d'exporter la carte en PDF. Réessayez ou utilisez l'impression.");
    } finally {
      setExportingPdf(false);
    }
  }, [member]);

  const footer = (
    <CardModalActions
      isAdminUi={isAdminUi}
      member={member}
      exportingPdf={exportingPdf}
      onPrint={handlePrint}
      onExportPdf={handleExportPdf}
      onEdit={onEdit}
      onHide={onHide}
    />
  );

  const body = (
    <div className="member-card-modal__preview">
      {loadingVisits ? (
        <div className="member-card-modal__loading">
          <Spinner animation="border" size="sm" />
          <span>Chargement de l&apos;historique des visites…</span>
        </div>
      ) : null}
      <MemberCardSheet
        member={member}
        visits={visits}
        companies={companies}
        getCompanyName={getCompanyName}
        formatDate={formatDate}
      />
    </div>
  );

  if (isAdminUi) {
    return (
      <AdminModal
        show={show}
        onHide={onHide}
        size="xl"
        icon="user"
        badge="Carte adhérent"
        title={fullName || 'Carte adhérent'}
        subtitle={member?.matricule ? `Matricule ${member.matricule}` : undefined}
        ariaLabelledBy="adm-member-card-title"
        footer={footer}
      >
        {body}
      </AdminModal>
    );
  }

  return (
    <AdminPersonnelModal
      show={show}
      onHide={onHide}
      size="xl"
      icon="user"
      badge="Carte adhérent"
      title={fullName || 'Carte adhérent'}
      subtitle={member?.matricule ? `Matricule ${member.matricule}` : undefined}
      ariaLabelledBy="ap-member-card-title"
      footer={footer}
    >
      {body}
    </AdminPersonnelModal>
  );
};

export default MemberDetailsModal;
